/**
 * The interruption card (components/Notices/spec.md § Anatomy, parameterised by
 * components/Failures/spec.md): severity, an optional title, one message, 0–2 actions. What a
 * case says and offers is the failure table's (components/Failures/failures.ts); this draws it.
 */
import {useEffect, useRef, type Ref} from 'react'
import {Link} from 'react-router'
import classNames from 'classnames'
import {Icon, type IconProps} from '@ui/Icon/icon'
import {roleFor, type ActionEmphasis, type FailureSeverity} from '@product/quies/components/Failures/failures'
import button from '@product/quies/styles/button.module.scss'
import styles from './Notices.module.scss'

type IconId = IconProps['id']

/** A press, a link (the pricing anchor), or a native POST (the sign-in route redirects to Google). */
export type CardActionTarget = (() => void) | {href: string} | {formAction: string}

export interface InterruptionCardAction {
    label: string
    emphasis: ActionEmphasis
    icon?: IconId
    target: CardActionTarget
    /** Called before the target runs — the failure_shown count. */
    onPress?: () => void
}

export interface InterruptionCardProps {
    severity: FailureSeverity
    title?: string
    message: string
    actions: InterruptionCardAction[]
    /** Focus the primary (last) action on appearing — never while audio plays (Failures spec
     *  § Accessibility). The focus never scrolls: the reader's scroll wins. */
    focusPrimary?: boolean
}

const SEVERITY_ICON: Record<FailureSeverity, IconId> = {
    info: 'qs-info',
    warning: 'qs-warning',
    error: 'qs-alert',
}

export function InterruptionCard({severity, title, message, actions, focusPrimary = false}: InterruptionCardProps) {
    const primaryRef = useRef<HTMLElement | null>(null)

    useEffect(() => {
        if (focusPrimary) primaryRef.current?.focus({preventScroll: true})
    }, [focusPrimary])

    return (
        <div className={styles.card} role={roleFor(severity, 'card')}>
            <Icon id={SEVERITY_ICON[severity]} size={28} outline className={styles.cardIcon} data-tone={severity} aria-hidden="true" />
            {title && <p className={styles.cardTitle}>{title}</p>}
            <p className={styles.cardBody}>{message}</p>
            {actions.length > 0 && (
                <div className={styles.cardActions}>
                    {actions.map((action, i) => (
                        <CardAction
                            key={action.label}
                            action={action}
                            ref={i === actions.length - 1 ? primaryRef : undefined}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function CardAction({action, ref}: {action: InterruptionCardAction; ref?: Ref<HTMLElement | null>}) {
    const className = classNames(button[action.emphasis], styles.cardAction)
    const content = (
        <>
            {action.icon && action.emphasis !== 'quiet' && <Icon id={action.icon} size={18} outline={!action.icon.endsWith('-fill')} aria-hidden="true" />}
            {action.label}
        </>
    )
    const {target} = action

    if (typeof target === 'function') {
        return (
            <button
                ref={ref as Ref<HTMLButtonElement>}
                type="button"
                className={className}
                onClick={() => { action.onPress?.(); target() }}
            >
                {content}
            </button>
        )
    }
    if ('href' in target) {
        return (
            <Link ref={ref as Ref<HTMLAnchorElement>} to={target.href} className={className} onClick={action.onPress}>
                {content}
            </Link>
        )
    }
    return (
        <form method="post" action={target.formAction} className={styles.cardForm} onSubmit={action.onPress}>
            <button ref={ref as Ref<HTMLButtonElement>} type="submit" className={className}>
                {content}
            </button>
        </form>
    )
}
